import {
  AutomationEndpoints,
  AutomationLogEntry,
  AutomationLogsEndpoints,
  AutomationRequest,
  AutomationRule,
  AutomationTemplate,
  AutomationTemplateEndpoints,
  CheckUsageOfInventoryIdsRequest,
  PaginatedResponse,
} from '@scaleaq/sitecontrol-lib';

import { API_NAMES, CACHE_KEYS } from '@/constants';
import {
  useAuthApiDeleteById,
  useAuthApiGetSingleFetch,
  useAuthApiPost,
  useAuthApiUpdate,
  useAuthApiUpdateById,
} from '@/dataAccess/rest/apiHooks';

import { TemplateAutomationFormType } from './automation.types';

const baseApiRoute = API_NAMES.automationApi;

//Logs
export const useLogs = () =>
  useAuthApiGetSingleFetch<PaginatedResponse<AutomationLogEntry>>({
    baseApiRoute,
    route: AutomationLogsEndpoints.getLogsV1,
    cacheKeys: [CACHE_KEYS.logs],
  });

export const useLogsByAutomationId = (id: string | undefined) =>
  useAuthApiGetSingleFetch<PaginatedResponse<AutomationLogEntry>>({
    baseApiRoute,
    route: AutomationLogsEndpoints.getLogsByAutomationIdV1.replace('{id}', id || ''),
    cacheKeys: [CACHE_KEYS.logs, id || ''],
    enabled: !!id,
  });

//Automation

export const useAutomations = () =>
  useAuthApiGetSingleFetch<AutomationRule[]>({
    baseApiRoute,
    route: AutomationEndpoints.getAutomationsV1,
    cacheKeys: [CACHE_KEYS.automation],
  });

export const useUpsertAutomation = (id: string | undefined, identifier: string = '') => {
  const { mutate: updateAutomation } = useAuthApiUpdate<AutomationRule>({
    baseApiRoute,
    route: AutomationEndpoints.updateAutomationV1.replace('/{id}', ''),
    cacheKeys: [CACHE_KEYS.automation],
    id: id,
    identifier,
  });

  const { mutate: addNewAutomation } = useAuthApiPost<AutomationRequest>({
    baseApiRoute,
    route: AutomationEndpoints.addAutomationV1,
    cacheKeys: [CACHE_KEYS.automation],
    identifier,
  });
  const upsertAutomation = (
    automation: AutomationRequest | AutomationRule,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    const isItUpdate = !!automation.id;
    if (isItUpdate) {
      return updateAutomation(automation as AutomationRule, options);
    }
    return addNewAutomation(automation as AutomationRequest, options);
  };

  return { upsertAutomation };
};

export const useUpdateAutomation = (automation: AutomationRule | null) => {
  return useAuthApiUpdate<AutomationRule>({
    baseApiRoute,
    route: AutomationEndpoints.updateAutomationV1.replace('/{id}', ''),
    id: automation?.id ?? '',
    cacheKeys: [CACHE_KEYS.automation],
    identifier: automation?.name,
  });
};
export const useUpsertTemplateAutomation = (id: string | undefined, identifier: string = '') => {
  const { mutate: updateTemplateAutomation } = useAuthApiUpdate<TemplateAutomationFormType>({
    baseApiRoute,
    route: AutomationEndpoints.updateTemplateBasedAutomationV1.replace('/{id}', ''),
    id: id ?? '',
    cacheKeys: [CACHE_KEYS.automation],
    identifier,
  });

  const { mutate: addNewTemplateAutomation } = useAuthApiPost<TemplateAutomationFormType>({
    baseApiRoute,
    route: AutomationEndpoints.addTemplateBasedAutomationV1,
    cacheKeys: [CACHE_KEYS.automation],
    identifier,
  });

  const upsertTemplateAutomation = (
    automation: Partial<TemplateAutomationFormType & AutomationRule>,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    const isItUpdate = !!automation.id;
    if (isItUpdate) {
      return updateTemplateAutomation(automation, options);
    }
    return addNewTemplateAutomation(automation, options);
  };

  return { upsertTemplateAutomation };
};

export const useDeleteAutomation = (identifier?: string) => {
  return useAuthApiDeleteById<AutomationRule & { id: string }>({
    baseApiRoute,
    route: AutomationEndpoints.deleteAutomationV1,
    cacheKeys: [CACHE_KEYS.automation],
    identifier: identifier,
  });
};

/**
 * This endpoint deactivates an automation rule
 * @param identifier id of the automation rule to deactivate
 * @returns {Function} - A function to call to deactivate the automation rule
 * @example
 * const { mutate: deactivateAutomation, isPending: deactivateAutomationLoading } =
 *     useDeactivateAutomation();
 */
export const useDeactivateAutomation = (identifier?: string) => {
  return useAuthApiUpdateById<AutomationRule>({
    baseApiRoute,
    route: AutomationEndpoints.deactivateAutomationV1,
    cacheKeys: ['automationDeactivateQueryKey'],
    identifier: identifier || API_NAMES.automationApi,
  });
};

/**
 *
 * @param identifier id of the automation rule to activate
 * @returns {Function} - A function to call to activate the automation rule
 * @example
 * const { mutate: activateAutomation, isPending: updateAutomationLoading } =
 *    useActivateAutomation();
 */
export const useActivateAutomation = (identifier?: string) => {
  return useAuthApiUpdateById<AutomationRule>({
    baseApiRoute,
    route: AutomationEndpoints.activateAutomationV1,
    cacheKeys: ['automationActivateQueryKey'],
    identifier: identifier || API_NAMES.automationApi,
  });
};

//Template

export const useTemplates = () =>
  useAuthApiGetSingleFetch<AutomationTemplate[]>({
    baseApiRoute,
    route: AutomationTemplateEndpoints.getAutomationTemplatesV1,
    cacheKeys: [CACHE_KEYS.templates],
  });

export const useCheckInventoriesUsage = () => {
  return useAuthApiPost<CheckUsageOfInventoryIdsRequest, Record<string, AutomationRule[]>>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.inventoryCheckUsage],
    returnJsonResult: true,
    route: AutomationEndpoints.getAutomationsBySensorIdManyV1,
  });
};
